import { useEffect, useState } from 'react';
import type { ChangeEmailRequest, User } from '@mockd/shared';
import { ApiError } from '../../utils/apiClient';
import { Modal } from '../common/Modal';
import { Alert } from '../common/Alert';

interface ChangeEmailModalProps {
  isOpen: boolean;
  user: Pick<User, 'email' | 'hasPassword'>;
  /** Resolves to the address the confirmation link was sent to. */
  onSubmit: (data: ChangeEmailRequest) => Promise<string>;
  /** Passwordless accounts confirm by signing in again. */
  onReauthenticate: () => void;
  onClose: () => void;
}

export function ChangeEmailModal({
  isOpen,
  user,
  onSubmit,
  onReauthenticate,
  onClose,
}: ChangeEmailModalProps) {
  const [newEmail, setNewEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [needsReauth, setNeedsReauth] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setNewEmail('');
      setCurrentPassword('');
      setError(null);
      setNeedsReauth(false);
      setPendingEmail(null);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setNeedsReauth(false);

    if (newEmail.trim().toLowerCase() === user.email) {
      setError('That is already your email address');
      return;
    }

    setIsSaving(true);
    try {
      setPendingEmail(await onSubmit({ newEmail: newEmail.trim(), ...(user.hasPassword && { currentPassword }) }));
    } catch (err) {
      if (err instanceof ApiError && err.code === 'REAUTH_REQUIRED') {
        setNeedsReauth(true);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to change your email');
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Change email" showCloseButton>
      {pendingEmail ? (
        <>
          <p className="text-sm text-base-content/80 mt-4">
            We sent a confirmation link to <span className="font-medium">{pendingEmail}</span>. Open it
            within 24 hours, while signed in to this account, to finish the change. Until then you
            keep signing in as <span className="font-medium">{user.email}</span>.
          </p>
          <div className="flex justify-end pt-6">
            <button type="button" onClick={onClose} className="btn btn-primary">
              Done
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="text-sm text-base-content/80 mt-4">
            We&apos;ll send a link to the new address. Your email changes once you open it.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            {error && <Alert variant="error">{error}</Alert>}

            {needsReauth && (
              <Alert variant="warning" role="alert">
                <div className="flex flex-col items-start gap-2">
                  <span>For your security, sign in again, then come back here to change your email.</span>
                  <button type="button" className="btn btn-sm" onClick={onReauthenticate}>
                    Sign in again
                  </button>
                </div>
              </Alert>
            )}

            <div className="fieldset">
              <label htmlFor="new-email" className="fieldset-legend">
                New email
              </label>
              <input
                id="new-email"
                type="email"
                autoComplete="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                className="input w-full"
                disabled={isSaving}
              />
            </div>

            {user.hasPassword && (
              <div className="fieldset">
                <label htmlFor="change-email-password" className="fieldset-legend">
                  Current password
                </label>
                <input
                  id="change-email-password"
                  type="password"
                  autoComplete="current-password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="input w-full"
                  disabled={isSaving}
                />
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={onClose} className="btn btn-ghost" disabled={isSaving}>
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSaving || !newEmail.trim() || (user.hasPassword && !currentPassword)}
              >
                {isSaving ? 'Sending…' : 'Send confirmation link'}
              </button>
            </div>
          </form>
        </>
      )}
    </Modal>
  );
}
